import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Order, OrderItem, MenuItem } from '../models';
import { OrderStatus, OrderItemStatus } from '../enums';
import { DatabaseService } from './database.service';
import { InsumosService } from './insumos.service';
import { tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  private ordersSubject = new BehaviorSubject<Order[]>([]);
  private menuItemsSubject = new BehaviorSubject<MenuItem[]>([]);

  public orders$: Observable<Order[]> = this.ordersSubject.asObservable();
  public menuItems$: Observable<MenuItem[]> = this.menuItemsSubject.asObservable();

  constructor(
    private database: DatabaseService,
    private insumosService: InsumosService
  ) {
    // Esperar un poco para que IndexedDB esté listo
    setTimeout(() => {
      this.loadData();
    }, 300);
  }

  private loadData(): void {
    this.reloadOrdersFromDb();

    // Cargar menú desde IndexedDB
    this.loadMenuData();
  }

  /** Recarga órdenes desde IndexedDB (útil para que Reportes tenga datos actualizados). */
  reloadOrdersFromDb(): void {
    this.database.getAllOrders().subscribe({
      next: (orders) => {
        const orderObjects = orders.map(o => this.deserializeOrder(o));
        this.ordersSubject.next(orderObjects);
      },
      error: (error) => {
        console.error('Error loading orders:', error);
        this.ordersSubject.next([]);
      }
    });
  }

  private loadMenuData(): void {
    this.database.getAllMenuItems().subscribe({
      next: (items) => {
        if (items.length === 0) {
          // Si no hay items, inicializar el menú por defecto
          this.initializeDefaultMenuItems();
        } else {
          const menuObjects = items.map(i => this.deserializeMenuItem(i));
          this.menuItemsSubject.next(menuObjects);
        }
      },
      error: (error) => {
        console.error('Error loading menu items:', error);
        this.menuItemsSubject.next([]);
        this.initializeDefaultMenuItems();
      }
    });
  }

  private deserializeOrder(data: any): Order {
    const order = new Order(data.id, data.tableId);
    order.linkedTableIds = data.linkedTableIds || [];
    order.status = data.status || OrderStatus.DRAFT;
    order.items = (data.items || []).map((item: any) => this.deserializeOrderItem(item));
    order.totalAmount = data.totalAmount || 0;
    order.tax = data.tax || 0;
    order.discount = data.discount || 0;
    order.notes = data.notes;
    order.qrCode = data.qrCode;
    order.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    order.confirmedAt = data.confirmedAt ? new Date(data.confirmedAt) : undefined;
    order.completedAt = data.completedAt ? new Date(data.completedAt) : undefined;
    order.updatedAt = data.updatedAt ? new Date(data.updatedAt) : new Date();
    return order;
  }

  private deserializeOrderItem(data: any): OrderItem {
    const item = new OrderItem(data.id, data.menuItemId, data.quantity, data.price);
    item.status = data.status || OrderItemStatus.PENDING;
    item.notes = data.notes;
    item.subtotal = data.subtotal || (data.quantity * data.price);
    item.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    item.updatedAt = data.updatedAt ? new Date(data.updatedAt) : new Date();
    return item;
  }

  private deserializeMenuItem(data: any): MenuItem {
    const item = new MenuItem(
      data.id,
      data.name,
      data.description,
      data.price,
      data.category,
      data.preparationTime || 15
    );
    item.image = data.image;
    item.available = data.available !== undefined ? data.available : true;
    item.availableQuantity = data.availableQuantity != null ? data.availableQuantity : null;
    item.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    item.updatedAt = data.updatedAt ? new Date(data.updatedAt) : new Date();
    return item;
  }

  private serializeOrder(order: Order): any {
    return {
      id: order.id,
      tableId: order.tableId,
      linkedTableIds: order.linkedTableIds,
      status: order.status,
      items: order.items.map(item => this.serializeOrderItem(item)),
      totalAmount: order.totalAmount,
      tax: order.tax,
      discount: order.discount,
      notes: order.notes,
      qrCode: order.qrCode,
      createdAt: order.createdAt.toISOString(),
      confirmedAt: order.confirmedAt?.toISOString(),
      completedAt: order.completedAt?.toISOString(),
      updatedAt: order.updatedAt.toISOString()
    };
  }

  private serializeOrderItem(item: OrderItem): any {
    return {
      id: item.id,
      menuItemId: item.menuItemId,
      quantity: item.quantity,
      status: item.status,
      notes: item.notes,
      price: item.price,
      subtotal: item.subtotal,
      createdAt: item.createdAt.toISOString(),
      updatedAt: item.updatedAt.toISOString()
    };
  }

  private serializeMenuItem(item: MenuItem): any {
    return {
      id: item.id,
      name: item.name,
      description: item.description,
      price: item.price,
      category: item.category,
      image: item.image,
      preparationTime: item.preparationTime,
      available: item.available,
      availableQuantity: item.availableQuantity ?? null,
      createdAt: item.createdAt.toISOString(),
      updatedAt: item.updatedAt.toISOString()
    };
  }

  // Métodos para Órdenes

  getOrders(): Order[] {
    return this.ordersSubject.value;
  }

  getOrderById(id: string): Order | undefined {
    return this.getOrders().find(order => order.id === id);
  }

  getOrdersByTableId(tableId: string): Order[] {
    return this.getOrders().filter(order =>
      order.tableId === tableId || order.linkedTableIds.includes(tableId)
    );
  }

  getActiveOrderForTable(tableId: string): Order | undefined {
    return this.getOrdersByTableId(tableId).find(order => !order.isCompleted());
  }

  /** True si la mesa tiene al menos un pedido activo (no completado, no facturado, no cancelado). */
  hasActiveNonInvoicedOrdersForTable(tableId: string): boolean {
    const orders = this.getOrdersByTableId(tableId);
    return orders.some(o =>
      o.status !== OrderStatus.COMPLETED &&
      o.status !== OrderStatus.INVOICED &&
      o.status !== OrderStatus.CANCELLED
    );
  }

  /** No se registran pedidos en borrador: solo se persisten con estado confirmado o en cocina. */
  createOrder(order: Order): Order {
    if (order.status === OrderStatus.DRAFT) {
      console.warn('No se registran pedidos en borrador. Asigne estado (confirmado/en cocina) antes de crear.');
      return order;
    }
    const serialized = this.serializeOrder(order);
    this.database.createOrder(serialized).subscribe({
      next: () => {
        const orders = [...this.getOrders(), order];
        this.ordersSubject.next(orders);
      },
      error: (error) => {
        console.error('Error creating order:', error);
      }
    });
    return order;
  }

  /** Añade una orden solo en memoria (simulador); no persiste en DB. */
  createOrderSimulation(order: Order): Order {
    order.isSimulation = true;
    const orders = [...this.getOrders(), order];
    this.ordersSubject.next(orders);
    return order;
  }

  /** Quita todas las órdenes marcadas como simulación (al terminar el simulador). */
  removeSimulationOrders(): void {
    const orders = this.getOrders().filter(o => !o.isSimulation);
    this.ordersSubject.next(orders);
  }

  /** No persiste órdenes de simulación (no deben dejar registros). */
  updateOrder(order: Order): Order {
    order.updatedAt = new Date();
    if (order.isSimulation) {
      const orders = this.getOrders();
      const index = orders.findIndex(o => o.id === order.id);
      if (index !== -1) {
        orders[index] = order;
        this.ordersSubject.next([...orders]);
      }
      return order;
    }
    const serialized = this.serializeOrder(order);
    this.database.updateOrder(serialized).subscribe({
      next: () => {
        const orders = this.getOrders();
        const index = orders.findIndex(o => o.id === order.id);
        if (index !== -1) {
          orders[index] = order;
          this.ordersSubject.next([...orders]);
        }
      },
      error: (error) => {
        console.error('Error updating order:', error);
      }
    });
    return order;
  }

  deleteOrder(id: string): void {
    this.database.deleteOrder(id).subscribe({
      next: () => {
        const orders = this.getOrders().filter(o => o.id !== id);
        this.ordersSubject.next(orders);
      },
      error: (error) => {
        console.error('Error deleting order:', error);
      }
    });
  }

  addItemToOrder(orderId: string, item: OrderItem): void {
    const order = this.getOrderById(orderId);
    if (order) {
      order.addItem(item);
      this.updateOrder(order);
    }
  }

  removeItemFromOrder(orderId: string, itemId: string): void {
    const order = this.getOrderById(orderId);
    if (order) {
      order.removeItem(itemId);
      this.updateOrder(order);
    }
  }

  updateOrderItemStatus(orderId: string, itemId: string, status: OrderItemStatus): void {
    const order = this.getOrderById(orderId);
    if (order) {
      const item = order.items.find(i => i.id === itemId);
      if (item) {
        item.updateStatus(status);
        this.updateOrder(order);
      }
    }
  }

  confirmOrder(orderId: string): void {
    const order = this.getOrderById(orderId);
    if (order) {
      order.confirm();
      this.updateOrder(order);
    }
  }

  sendOrderToKitchen(orderId: string): void {
    const order = this.getOrderById(orderId);
    if (order) {
      order.sendToKitchen();
      order.items.forEach(item => item.updateStatus(OrderItemStatus.IN_PREPARATION));
      this.updateOrder(order);
    }
  }

  markOrderReadyToServe(orderId: string): void {
    const order = this.getOrderById(orderId);
    if (order) {
      order.markReadyToServe();
      order.items.forEach(item => item.updateStatus(OrderItemStatus.READY));
      this.updateOrder(order);
    }
  }

  completeOrder(orderId: string): void {
    const order = this.getOrderById(orderId);
    if (order) {
      order.complete();
      order.items.forEach(item => {
        item.updateStatus(OrderItemStatus.DELIVERED);
        this.decreaseMenuItemAvailableQuantity(item.menuItemId, item.quantity);
        this.insumosService.decreaseInsumosForMenuItem(item.menuItemId, item.quantity);
      });
      this.updateOrder(order);
    }
  }

  /** Disminuye la cantidad disponible de un plato al entregar (solo si tiene disponible definido). */
  decreaseMenuItemAvailableQuantity(menuItemId: string, quantity: number): void {
    const item = this.getMenuItems().find(m => m.id === menuItemId);
    if (!item || item.availableQuantity == null) return;
    item.availableQuantity = Math.max(0, item.availableQuantity - quantity);
    item.updatedAt = new Date();
    this.updateMenuItem(item);
  }

  cancelOrder(orderId: string): void {
    const order = this.getOrderById(orderId);
    if (order) {
      order.cancel();
      this.updateOrder(order);
    }
  }

  /** Marca las órdenes como facturadas (ya incluidas en una factura). También reduce cantidad disponible del menú e insumos. */
  markOrdersAsInvoiced(orderIds: string[]): void {
    orderIds.forEach(orderId => {
      const order = this.getOrderById(orderId);
      if (order) {
        order.items.forEach(item => {
          this.decreaseMenuItemAvailableQuantity(item.menuItemId, item.quantity);
          this.insumosService.decreaseInsumosForMenuItem(item.menuItemId, item.quantity);
        });
        order.status = OrderStatus.INVOICED;
        order.updatedAt = new Date();
        this.updateOrder(order);
      }
    });
  }

  // Métodos para Menú

  getMenuItems(): MenuItem[] {
    return this.menuItemsSubject.value;
  }

  getMenuItemById(id: string): MenuItem | undefined {
    return this.getMenuItems().find(item => item.id === id);
  }

  getMenuItemsByCategory(category: string): MenuItem[] {
    return this.getMenuItems().filter(item => item.category === category);
  }

  getCategories(): string[] {
    const categories = new Set(this.getMenuItems().map(item => item.category));
    return Array.from(categories);
  }

  createMenuItem(item: MenuItem): MenuItem {
    const serialized = this.serializeMenuItem(item);
    this.database.createMenuItem(serialized).subscribe({
      next: () => {
        const items = [...this.getMenuItems(), item];
        this.menuItemsSubject.next(items);
      },
      error: (error) => {
        console.error('Error creating menu item:', error);
      }
    });
    return item;
  }

  updateMenuItem(item: MenuItem): MenuItem {
    item.updatedAt = new Date();
    const serialized = this.serializeMenuItem(item);
    this.database.updateMenuItem(serialized).subscribe({
      next: () => {
        const items = this.getMenuItems();
        const index = items.findIndex(i => i.id === item.id);
        if (index !== -1) {
          items[index] = item;
          this.menuItemsSubject.next([...items]);
        }
      },
      error: (error) => {
        console.error('Error updating menu item:', error);
      }
    });
    return item;
  }

  deleteMenuItem(id: string): void {
    this.database.deleteMenuItem(id).subscribe({
      next: () => {
        const items = this.getMenuItems().filter(i => i.id !== id);
        this.menuItemsSubject.next(items);
      },
      error: (error) => {
        console.error('Error deleting menu item:', error);
      }
    });
  }

  private initializeDefaultMenuItems(): void {
    const defaultMenuItems: MenuItem[] = [
      new MenuItem('item_1', 'Ceviche de Camarones', 'Camarones frescos con limón y cebolla', 28500, 'Entradas', 10),
      new MenuItem('item_2', 'Causa Limeña', 'Papa amarilla con atún y mayonesa', 18500, 'Entradas', 8),
      new MenuItem('item_3', 'Anticuchos', 'Brochetas de res marinadas', 22000, 'Entradas', 12),
      new MenuItem('item_4', 'Lomo Saltado', 'Lomo de res salteado con papas', 35000, 'Principales', 20),
      new MenuItem('item_5', 'Ají de Gallina', 'Pollo en salsa de ají amarillo', 28000, 'Principales', 18),
      new MenuItem('item_6', 'Ceviche Mixto', 'Mezcla de mariscos frescos', 32000, 'Principales', 15),
      new MenuItem('item_7', 'Arroz con Pollo', 'Pollo en salsa con arroz', 24500, 'Principales', 15),
      new MenuItem('item_8', 'Flan Casero', 'Postre de caramelo casero', 12000, 'Postres', 5),
      new MenuItem('item_9', 'Helado de Lúcuma', 'Helado de fruta tropical', 9500, 'Postres', 3),
      new MenuItem('item_10', 'Chicha Morada', 'Bebida tradicional andina', 5500, 'Bebidas', 2),
      new MenuItem('item_11', 'Café Colombiano', 'Café de altura', 4500, 'Bebidas', 3),
    ];

    // Guardar cada item en IndexedDB
    defaultMenuItems.forEach(item => {
      const serialized = this.serializeMenuItem(item);
      this.database.createMenuItem(serialized).subscribe({
        error: (error) => {
          console.error('Error creating default menu item:', error);
        }
      });
    });

    this.menuItemsSubject.next(defaultMenuItems);
  }

  resetData(): void {
    this.ordersSubject.next([]);
    this.menuItemsSubject.next([]);
    // Los datos se limpian desde DataInitService
  }
}
